import type {
  
  IBaseSkuCharacteristicValue,
  IImageSkuCharacteristic,
  TSkuCharacteristic,
} from '../models/sku-characteristic';
import { ESkuCharacteristicType } from '../models/sku-characteristic';
import { CharacteristicType, type ProductPageQuery } from '@/shared/apollo-api/__generated__/types';

interface IProductToSkuCharacteristicMapperOptions {
  sortEnabled: boolean;
}

const defaultOptions = { sortEnabled: true };

/**
 * Функция преобразует характеристики продукта. Сортирует отсутствующие по необходимости. Функция используется для ску селектора
 * @param {IProduct} product - Выбранный продукт.
 * @param {IProductToSkuCharacteristicMapperOptions} options - объект опций маппинга.
 * @property {boolean} options.sortEnabled - Если true, то сортирует отсутствующие на складе характеристики в конец
 * @return {TSkuCharacteristic[]} Объект корзины.
 */
export const productCharacteristicMapper = (
  product: ProductPageQuery['product'],
  options: IProductToSkuCharacteristicMapperOptions = defaultOptions,
): TSkuCharacteristic[] => {
  const skuList = product.skuList;
  const { sortEnabled } = options;
  const sortValues = (a: IBaseSkuCharacteristicValue, b: IBaseSkuCharacteristicValue) =>
    Number(a.disabled) - Number(b.disabled);

  function isCharacteristicValueDisabled(characteristicValueId: number) {
    const availableAmount = skuList.reduce((amount, sku) => {
      const hasChar = sku.characteristicValues.some((value) => value.id === characteristicValueId);

      return hasChar ? amount + sku.availableAmount : amount;
    }, 0);

    return availableAmount <= 0;
  }

  return product.characteristics?.map((characteristic) => {
    // Фильтруем храктериситики, для которых нет ни одного sku
    const values = characteristic.values.filter((charValue) =>
      skuList.reduce((hasChar, sku) => {
        return hasChar ? hasChar : sku.characteristicValues.some((value) => value.id === charValue.id);
      }, false),
    );

    switch (characteristic.type) {
      case CharacteristicType.Color: {
        const colorValues = values.map((value) => ({
          mainColor: value.value,
          valueId: String(value.id),
          text: value.title,
          disabled: isCharacteristicValueDisabled(value.id),
        }));

        return {
          characteristicId: String(characteristic.id),
          type: ESkuCharacteristicType.COLOR,
          title: characteristic.title,
          values: sortEnabled ? colorValues.sort(sortValues) : colorValues,
        };
      }
      case CharacteristicType.Image: {
        const imageValues = values.map((value) => ({
          mainColor: value.value,
          valueId: String(value.id),
          text: value.title,
          imageLink: value.photo?.link.high,
          color: value.value,
          disabled: isCharacteristicValueDisabled(value.id),
          photoKey: value.photo?.key,
        }));

        const val: IImageSkuCharacteristic = {
          characteristicId: String(characteristic.id),
          type: ESkuCharacteristicType.IMAGE,
          title: characteristic.title,
          values: (sortEnabled ? imageValues.sort(sortValues) : imageValues) as IImageSkuCharacteristic['values'],
        };

        return val;
      }

      case CharacteristicType.Text: {
        const textValues = values.map((value) => ({
          mainColor: value.value,
          valueId: String(value.id),
          text: value.title,
          disabled: isCharacteristicValueDisabled(value.id),
        }));

        return {
          characteristicId: String(characteristic.id),
          type: ESkuCharacteristicType.TEXT,
          title: characteristic.title,
          values: sortEnabled ? textValues.sort(sortValues) : textValues,
        };
      }
    }
  });
};
